import { Box, Stack, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { PublicFormButton, PublicFormCard } from "../../../components/common/form/PublicFormCard";
import { MESSAGES } from "../../../modules/common/shared/messages";
import { Student } from "../../../modules/common/shared/types";
import { StudentAdmissionForm, SubmittedAdmission } from "../components/StudentAdmissionForm";

// Router state survives a page refresh, so on success the verified `student` is replaced
// with `submitted`: a refresh then shows the confirmation again, never the form.
type AdmissionApplyState = { student?: Student; submitted?: SubmittedAdmission };

export const AdmissionApply = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as AdmissionApplyState | null;

  if (state?.submitted) {
    return <AdmissionSubmitted submitted={state.submitted} />;
  }

  // Student data only exists once /admission/verify has succeeded and handed it off via
  // router state; direct/refreshed navigation here has nothing to admit, so send them back.
  // This check has to happen before the form's hooks are declared (rules of hooks),
  // hence the split into a separate form component rather than an early return inside it.
  if (!state?.student) {
    return <Navigate to="/enquiry/verify" replace />;
  }

  return (
    <StudentAdmissionForm
      mode="PARENT"
      inputStudent={state.student}
      onParentSubmitted={(submitted) => {
        navigate(location.pathname, { replace: true, state: { submitted } });
        // the form is long; bring the confirmation card into view
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
    />
  );
};

const AdmissionSubmitted = ({ submitted }: { submitted: SubmittedAdmission }) => {
  const navigate = useNavigate();

  return (
    <PublicFormCard
      icon={<CheckCircleRoundedIcon />}
      tone="success"
      title="Admission request submitted"
      subtitle={MESSAGES.ADMISSION_FORM_SUBMITTED}
      maxWidth={480}
    >
      {/* What was submitted, so the parent can confirm at a glance */}
      <Stack spacing={1.25}>
        {[
          { label: "Student", value: submitted.name },
          { label: "Grade", value: submitted.gradeName },
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              bgcolor: "#f8fafc",
            }}
          >
            <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>{label}</Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a", textAlign: "right" }}>{value || "—"}</Typography>
          </Box>
        ))}
      </Stack>
      <PublicFormButton sx={{ mt: 3 }} onClick={() => navigate("/")}>
        Back to home
      </PublicFormButton>
    </PublicFormCard>
  );
};
